import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios'
import { env } from '@/env'

const TOKEN_STORAGE_KEY = 'token'

/**
 * A request that failed, carrying the message the API returned.
 *
 * The Quasar client swallowed errors and returned `[data, err]` tuples, then
 * re-showed the same toast at every call site. Here the client throws, one
 * place turns that into a toast (see queryClient's cache handlers), and
 * TanStack Query gets the retry/stale semantics it expects.
 */
export class ApiError extends Error {
  readonly status: number | undefined
  readonly cause: unknown
  /**
   * The parsed response body, when the API sent one.
   *
   * Some refusals carry more than prose: entering a giveaway without a linked
   * Discord account comes back with the platform and its `auth_url`, so the
   * client can offer the way out instead of only reporting the dead end.
   */
  readonly data: unknown

  constructor(message: string, status: number | undefined, cause: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.cause = cause
    this.data = axios.isAxiosError(cause) ? cause.response?.data : undefined
  }

  get isUnauthorized(): boolean {
    return this.status === 401 || this.status === 403
  }

  /**
   * The platform this request needs linked, if the API said so. `auth_url` is
   * where an unlinked user goes to connect it — held on the platform row
   * server-side, so a new platform needs no client release.
   */
  get linkRequired(): { name: string; auth_url: string } | null {
    if (!this.data || typeof this.data !== 'object') return null
    const platform = (this.data as Record<string, unknown>).external_platform
    if (!platform || typeof platform !== 'object') return null
    const { name, auth_url } = platform as Record<string, unknown>
    if (typeof name !== 'string' || typeof auth_url !== 'string' || !auth_url) return null
    return { name, auth_url }
  }
}

/**
 * The API replies with either a bare string body or `{ message }`.
 * `genErrorText` in the original read `e.response.data` directly, which
 * rendered "[object Object]" for the latter and threw outright when the
 * request never reached the server.
 */
function messageFrom(error: unknown): string {
  // Duck-typed rather than `instanceof AxiosError`: bundlers can hand the app
  // and its dependencies two different copies of axios, and then the class
  // identity check silently fails for every error.
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error.message : 'Something went wrong.'
  }
  const data = (error as AxiosError).response?.data
  if (typeof data === 'string' && data.trim().length > 0) return data
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>
    for (const key of ['message', 'error', 'detail'] as const) {
      if (typeof record[key] === 'string') return record[key] as string
    }
  }
  if (error.code === 'ERR_NETWORK') return 'Cannot reach the server. Check your connection.'
  if (error.code === 'ECONNABORTED') return 'The request timed out.'
  return error.message || 'Something went wrong.'
}

export function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY)
  } catch {
    // Private-mode Safari and some WebViews throw on localStorage access.
    return null
  }
}

export function writeToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(TOKEN_STORAGE_KEY)
    else localStorage.setItem(TOKEN_STORAGE_KEY, token)
  } catch {
    /* non-fatal: the session simply will not survive a reload */
  }
}

export const http: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 20_000,
})

// Read the token per-request rather than pinning it at boot, so signing in and
// signing out take effect without reloading (the Quasar app had to reassign
// `defaults.headers.common` by hand in three places).
http.interceptors.request.use((config) => {
  const token = readToken()
  if (token) config.headers.set('Authorization', `Bearer ${token}`)
  return config
})

http.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined
    return Promise.reject(new ApiError(messageFrom(error), status, error))
  },
)

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await http.get<T>(url, config)
  return data
}

export async function post<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const { data } = await http.post<T>(url, body, config)
  return data
}

export async function put<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await http.put<T>(url, body, config)
  return data
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await http.delete<T>(url, config)
  return data
}
